import { submitCalls, cloudCalls, listCalls, seatsCalls, listFields } from './gate';
import type { PlanRules } from './plan/planTypes';
import type { ValidationRule } from '@/types/schema';

export const OWNER_FIELD = 'ownerId';

export interface PublishedSchema {
  name: string;
  fields: { name: string; type: 'string'; validation?: ValidationRule[] }[];
  webBookable?: boolean;
  /** Always stated, never left to the `?? 'public'` default — see SUBMISSION_ACCESS. */
  ownership?: 'public' | 'user' | 'ownerWrite';
  ownerField?: string;
  subscriberList?: boolean;
  /** Server-enforced limits for this collection — capacity, deadline, one-per-person,
   *  waitlist, public counts. Enforced in crud.ts; see infra/lambda/shared/appRules.ts. */
  appRules?: PlanRules;
}


/**
 * What an app's own code says its collections are.
 *
 * Pure analysis of a source string, and it lived in `saddleHost.ts` — the client publish
 * orchestration, which imports the project store. So the one thing the SERVER needed in
 * order to derive an app's schemas from the document it already receives was locked
 * inside the client, and the gate it depends on had to ship in the binary to keep it
 * company. Same shape as the date token: a pure function trapped in a place that could
 * not travel.
 */

/**
 * Every collection an app needs declared server-side, and the access rule each one gets.
 *
 * Three kinds, and the rule is the difference between them:
 *  - `S.submit`    → webBookable: anyone may add, nobody may read. A waitlist.
 *  - `S.cloud`     → ownership 'user': the server stamps the owner and filters list reads
 *                    to the caller's own rows, so one person's data is theirs alone.
 *
 * `S.subscribe` needs nothing here: it posts to the platform's own double opt-in route,
 * which owns the subscriber list for the whole app rather than a per-entity collection.
 */
export function publishedSchemas(source: string): PublishedSchema[] {
  const out: PublishedSchema[] = submitSchemas(source);

  for (const call of cloudCalls(source)) {
    out.push({
      name: call.entity,
      fields: [...call.fields, OWNER_FIELD].map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'user',
      ownerField: OWNER_FIELD,
    });
  }

  // `S.list` collections are the owner's content that visitors read — a rota, a timetable.
  // The fields are the ones the APP reads, recorded so the owner is offered the right
  // columns rather than having to guess: an app reading `session.limit` while the owner
  // types `places` shows "0 places left" forever, with nothing saying why. They are
  // advisory — `ownerWrite` accepts columns the owner adds later, which is the point of it.
  const expected = listFields(source);
  for (const name of listCalls(source)) {
    if (out.some((sc) => sc.name === name)) continue;
    out.push({
      name,
      fields: (expected[name] ?? []).map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'ownerWrite',
    });
  }

  return out;
}

/**
 * Attaches the plan's server-enforced rules to the collection the app submits into.
 *
 * This is the step that was missing: the enforcement in crud.ts has been written and
 * tested since the appRules work, and nothing ever declared a rule, so no published app
 * has ever had a capacity that held. A limit only exists once it is on the schema.
 */
export function withAppRules(schemas: PublishedSchema[], rules: PlanRules | null | undefined): PublishedSchema[] {
  if (!rules || !Object.keys(rules).length) return schemas;
  // Only the write-only collection takes them: per-user cloud rows and the subscriber
  // list are not the thing being rationed.
  const target = schemas.find((sc) => sc.webBookable);
  if (!target) return schemas;
  return schemas.map((sc) => (sc === target ? { ...sc, appRules: rules } : sc));
}

/**
 * An app that shows "4 spots left" needs the counts endpoint open, and the counts
 * endpoint only exists when the collection declares `countBy`.
 *
 * Derived from the app's own code rather than trusted from the plan: the page is what
 * decides whether it displays availability, and a plan whose capacity was grouped by one
 * field while the app asks about another would leave `S.seats` reading an endpoint that
 * returns nothing. The grouping follows the capacity rule, which is what the count is
 * about; failing that, the field the app groups its submissions by.
 */
/**
 * Declare the owner's collections the PLAN promised, whether or not the app used them.
 *
 * `publishedSchemas` reads the app's own `S.list` calls, which is right — but a build that
 * reached for `S.store` instead leaves the owner with nothing to edit and no sign of why.
 * Declaring them anyway means the Live tab still offers the right columns, and a re-publish
 * of a corrected app finds its collection already there with its rows intact.
 */
export function withManaged(
  schemas: PublishedSchema[],
  manages: { collection: string; fields: string[] }[] | undefined,
): PublishedSchema[] {
  const out = [...schemas];
  for (const m of manages ?? []) {
    if (out.some((sc) => sc.name === m.collection)) continue;
    out.push({
      name: m.collection,
      fields: m.fields.map((f) => ({ name: f, type: 'string' as const })),
      ownership: 'ownerWrite',
    });
  }
  return out;
}

export function withCounts(schemas: PublishedSchema[], source: string): PublishedSchema[] {
  const wanted = seatsCalls(source);
  if (!wanted.length) return schemas;

  return schemas.map((sc) => {
    if (!wanted.includes(sc.name)) return sc;
    const rules = sc.appRules ?? {};
    if (rules.countBy) return sc;
    const groupBy = rules.capacity?.groupBy;
    // No grouping anywhere means one bucket for the whole collection, which the server
    // reports under a single key — still useful for "12 people have signed up".
    return { ...sc, appRules: { ...rules, countBy: groupBy ?? sc.fields[0]?.name ?? 'id' } };
  });
}

/**
 * A submission collection's access, stated rather than defaulted.
 *
 * These rows carried no `ownership` at all and worked, because `checkAccess` reads
 * `schema.ownership ?? 'public'` and `public` + `webBookable` happens to be exactly right
 * for a form: `webBookable` short-circuits the anonymous create, `mayReadSubmissions` keeps
 * reads to the owner, and (since SEC-13) only the app's managers may change an existing row.
 *
 * Right by accident is the problem. `public` changed meaning twice in one week — it used to
 * let any signed-in person edit or delete any row — and both times these collections would
 * have changed behaviour without a line of their own being touched. Twice more this week we
 * were punished for what an ABSENT field means: `webBookable` missing meant every submission
 * readable by anyone, `listed` missing meant listed. A census found 30 of 77 live schema rows
 * with no ownership value, the largest single group.
 *
 * So it says what it means. `ownerWrite` would be wrong here despite sounding closer — it
 * refuses a create without a user, which is the whole point of a public form.
 */
const SUBMISSION_ACCESS = { ownership: 'public' as const, webBookable: true as const };

/**
 * How long one answer on a public form may be.
 *
 * Generated apps declared every field as `{ name, type: 'string' }` with NO validation, so
 * nothing bounded a submission at all: `email: "not an email"` stored happily and a name
 * field took forty thousand characters. No error anywhere — the owner meets it in their own
 * list. And because nothing bounds a field, nothing bounds the row, so nothing bounds the
 * write cost either, which matters on an account whose Lambda concurrency is 10.
 *
 * 4,000 characters per field: far longer than any real answer to a form question — a long
 * "anything else we should know" is a few hundred — and short enough that a whole row stays
 * a few kilobytes rather than a hundred.
 *
 * ONLY LENGTH, DELIBERATELY NO `pattern`. `validateField` runs `new RegExp(rule.value)`,
 * which is precisely the shape SEC-18c is about: a pathological pattern holding a Lambda
 * for a minute, and ten form posts taking an account of concurrency 10 down. Today no
 * generated app can carry a pattern, which is the only reason that hole is unreachable —
 * so emitting one here would be building the reachable path to somebody else's live
 * vulnerability. `maxLength` is a plain length comparison with no such cost. Email FORMAT
 * is worth having and is not worth a regex on this path.
 */
const MAX_FIELD_CHARS = 4000;

function boundedField(name: string): { name: string; type: 'string'; validation: ValidationRule[] } {
  return {
    name,
    type: 'string' as const,
    validation: [{
      type: 'maxLength' as const,
      value: MAX_FIELD_CHARS,
      // The person reading this is filling in a form on somebody's page, so it says what
      // to do rather than naming a limit they never agreed to.
      message: 'That answer is too long — please shorten it.',
    }],
  };
}

export function submitSchemas(source: string): {
  name: string;
  fields: { name: string; type: 'string'; validation?: ValidationRule[] }[];
  webBookable: true;
  ownership: 'public';
}[] {
  const byEntity = new Map<string, Set<string>>();
  for (const call of submitCalls(source)) {
    const fields = byEntity.get(call.entity) ?? new Set<string>();
    for (const f of call.fields ?? []) fields.add(f);
    byEntity.set(call.entity, fields);
  }
  return [...byEntity].map(([name, fields]) => ({
    name,
    // Bounded, because these answers come from strangers on a public page. The owner's own
    // collections below are not: an owner's rota row or a person's journal entry can
    // legitimately be long, and nobody abuses their own app.
    fields: [...fields].map(boundedField),
    ...SUBMISSION_ACCESS,
  }));
}

